import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import {
  PhishingCharacteristic,
  PhishingCharacteristicTranslation,
  SyncPhishingCharacteristicItem,
} from '@/client/campaigns';
import { phishingContentLanguages } from '@/common/constants/languages';
import {
  usePhishingCharacteristics,
  useUpdatePhishingCharacteristicsList,
} from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

export type LocalCharacteristic = {
  rowId: string;
  id?: string;
  descriptions: Record<LanguagesEnum, string>;
};

export const createEmptyDescriptions = (): Record<LanguagesEnum, string> =>
  phishingContentLanguages.reduce(
    (descriptions, language) => ({ ...descriptions, [language]: '' }),
    {} as Record<LanguagesEnum, string>,
  );

export const mapServerToLocal = (
  characteristics: PhishingCharacteristic[],
): LocalCharacteristic[] =>
  characteristics.map((characteristic) => ({
    rowId: characteristic.id,
    id: characteristic.id,
    descriptions: (characteristic.translations ?? []).reduce(
      (descriptions, translation) => ({
        ...descriptions,
        [translation.language]: translation.description ?? '',
      }),
      createEmptyDescriptions(),
    ),
  }));

export const mapLocalToPayload = (
  characteristics: LocalCharacteristic[],
): SyncPhishingCharacteristicItem[] =>
  characteristics.map((characteristic) => {
    const languages = Array.from(
      new Set<LanguagesEnum>([
        ...phishingContentLanguages,
        ...(Object.keys(characteristic.descriptions) as LanguagesEnum[]),
      ]),
    );

    const translations: PhishingCharacteristicTranslation[] = languages.map(
      (language) => {
        const description = (
          characteristic.descriptions[language] ?? ''
        ).trim();

        return {
          language,
          description: description.length > 0 ? description : null,
        };
      },
    );

    return {
      ...(characteristic.id ? { id: characteristic.id } : {}),
      translations,
    };
  });

export const usePhishingCharacteristicsEditor = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const { phishingCharacteristics, isLoading, refetch } =
    usePhishingCharacteristics();
  const { update, isLoading: isUpdating } =
    useUpdatePhishingCharacteristicsList();

  const [characteristicsCached, setCharacteristicsCached] = useState<
    LocalCharacteristic[]
  >([]);
  const [characteristics, setCharacteristics] = useState<LocalCharacteristic[]>(
    [],
  );
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    const mapped = mapServerToLocal(phishingCharacteristics?.result ?? []);
    setCharacteristics(mapped);
    setCharacteristicsCached(mapped);
  }, [phishingCharacteristics?.result]);

  useEffect(() => {
    setIsChanged(!isEqual(characteristics, characteristicsCached));
  }, [characteristics, characteristicsCached]);

  const isSaving = isUpdating;

  const handleAddRow = (): void => {
    setCharacteristics((current) => [
      ...current,
      { rowId: v4(), descriptions: createEmptyDescriptions() },
    ]);
  };

  const handleDeleteRow = (rowId: string): void => {
    setCharacteristics((current) =>
      current.filter((characteristic) => characteristic.rowId !== rowId),
    );
  };

  const handleChange = (
    rowId: string,
    language: LanguagesEnum,
    value: string,
  ): void => {
    setCharacteristics((current) =>
      current.map((characteristic) =>
        characteristic.rowId === rowId
          ? {
              ...characteristic,
              descriptions: {
                ...characteristic.descriptions,
                [language]: value,
              },
            }
          : characteristic,
      ),
    );
  };

  const handleSave = async (): Promise<void> => {
    try {
      await update({ characteristics: mapLocalToPayload(characteristics) });
      toast?.success(
        t('toast.success'),
        t('globalSettings.phishingCharacteristics.saved'),
      );
      await refetch();
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const handleCancel = (): void => {
    setCharacteristics(characteristicsCached);
  };

  return {
    characteristics,
    isLoading,
    isChanged,
    isSaving,
    handleSave,
    handleCancel,
    handleAddRow,
    handleDeleteRow,
    handleChange,
  };
};
